import React, { useState, useEffect } from 'react';
import { Save, Smartphone, Zap, MapPin, Phone, ShieldCheck, Coins, MessageCircle, Database, AlertTriangle, X } from 'lucide-react';
import RadiusMapModal from './RadiusMapModal/RadiusMapModal';
import TimeSlotsSettings from './TimeSlotsSettings';
import { settingsAPI } from '../../../config/settings-api';
import { authAPI } from '../../../config/auth-api';
import { toast } from 'react-toastify';
import './Settings.css';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { IS_ALFAJR } from '../../../constants/general';
import { helpers } from '../../../utils/helpers';

const mapCoinsFromConfig = (coins = {}) => {
  if (IS_ALFAJR) {
    return {
      coinToQar: coins.coinToQar ?? 0,
      qarToCoin: coins.qarToCoin ?? 0,
      minCoinsRedeem: coins.minCoinsRedeem ?? 0,
      redeem: {
        limit1: coins.redeem?.limit1 ?? 0,
        limit2: coins.redeem?.limit2 ?? 0,
        redeem1: coins.redeem?.redeem1 ?? 0,
        redeem2: coins.redeem?.redeem2 ?? 0,
        redeem3: coins.redeem?.redeem3 ?? 0,
      },
    };
  }
  return {
    coinToQar: coins.coinToQar ?? 0,
    qarToCoin: coins.qarToCoin ?? 0,
    minCoinsRedeem: coins.minCoinsRedeem ?? 0,
    redeemScale: coins.redeemScale ?? 0,
  };
};

const mapExpressFromConfig = (expressDelivery = {}) => {
  if (IS_ALFAJR) {
    return {
      status: expressDelivery.status || false,
      startTime: expressDelivery.startTime || '10:00',
      endTime: expressDelivery.endTime || '22:00',
      range: expressDelivery.range ?? 10,
      charge: expressDelivery.charge ?? 10,
    };
  }
  return {
    status: expressDelivery.status || false,
    startTime: expressDelivery.startTime || '00:00',
    endTime: expressDelivery.endTime || '00:00',
    radius: expressDelivery.radius || 0,
    staffCount: expressDelivery.staffCount || 1,
    charge1: expressDelivery.charge1 || 0,
    charge2: expressDelivery.charge2 || 0,
    charge3: expressDelivery.charge3 || 0,
    paused: expressDelivery.paused || false,
    pausedAt: expressDelivery.pausedAt || null,
  };
};

const mapChatFromConfig = (chatConfig = {}) => ({
  status: chatConfig.status ?? true,
  startTime: chatConfig.startTime || '09:00',
  endTime: chatConfig.endTime || '18:00',
});

const Settings = ({ adminData }) => {
  const currentAdminData = adminData || authAPI.getAdminData();
  const isFajr2 = !IS_ALFAJR || (currentAdminData?.currentStore?.storeName === 'Al Fajr 2' || currentAdminData?.currentStore?.name === 'Al Fajr 2');
  const canManage = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.MANAGE_SETTINGS);

  const [loading, setLoading] = useState(false);
  const [expressSaving, setExpressSaving] = useState(false);
  const [versionSaving, setVersionSaving] = useState(false);
  const [storeContactSaving, setStoreContactSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [alertSaving, setAlertSaving] = useState(false);
  const [otpSaving, setOtpSaving] = useState(false);
  const [coinSaving, setCoinSaving] = useState(false);
  const [showRadiusModal, setShowRadiusModal] = useState(false);
  const [coins, setCoins] = useState(() => mapCoinsFromConfig());
  const [deliverySettings, setDeliverySettings] = useState({
    maxDeliveryLimit: 0,
    freeDeliveryTotal: 0,
    minOrderValue: 0,
    minCharge: 0,
    minChargeLimit: 0,
    perKm: 0
  });
  const [deliverySaving, setDeliverySaving] = useState(false);

  const [expressDelivery, setExpressDelivery] = useState(() => mapExpressFromConfig());
  const [chatConfig, setChatConfig] = useState(() => mapChatFromConfig());
  const [chatSaving, setChatSaving] = useState(false);
  const [appVersions, setAppVersions] = useState({
    androidAppVersion: '',
    iosAppVersion: ''
  });
  const [storeContact, setStoreContact] = useState({
    phone: '',
    whatsapp: ''
  });
  const [alerts, setAlerts] = useState({
    enquiryAlert: true,
    messageAlert: true,
    orderAlert: true,
    serverErrorAlert: true
  });
  const [otpConfig, setOtpConfig] = useState({
    haveOtpVerification: true,
    whatsappOtpVerification: false
  });
  const [cacheClearing, setCacheClearing] = useState(false);
  const [showClearCacheConfirm, setShowClearCacheConfirm] = useState(false);

  useEffect(() => {
    fetchGeneralConfigs();
    loadAdminAlerts();
  }, []);

  const loadAdminAlerts = () => {
    const adminData = authAPI.getAdminData();
    if (adminData) {
      const storedAlerts = adminData.webAlerts || {};
      setAlerts({
        enquiryAlert: storedAlerts.enquiryAlert ?? true,
        messageAlert: storedAlerts.messageAlert ?? true,
        orderAlert: storedAlerts.orderAlert ?? true,
        serverErrorAlert: storedAlerts.serverErrorAlert ?? true
      });
    }
  };

  const fetchGeneralConfigs = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAllGeneralConfigs();
      if (response.success && response.data) {
        setConfig(response.data);
        setExpressDelivery(mapExpressFromConfig(response.data.expressDelivery));
        setChatConfig(mapChatFromConfig(response.data.chatConfig));
        setAppVersions({
          androidAppVersion: response.data.androidAppVersion || '',
          iosAppVersion: response.data.iosAppVersion || ''
        });
        setStoreContact({
          phone: response.data.storeContact?.phone || '',
          whatsapp: response.data.storeContact?.whatsapp || ''
        });
        setOtpConfig({
          haveOtpVerification: response.data.haveOtpVerification ?? true,
          whatsappOtpVerification: response.data.whatsappOtpVerification ?? false
        });
        setCoins(mapCoinsFromConfig(response.data.coins));
        setDeliverySettings({
          maxDeliveryLimit: response.data.maxDeliveryLimit ?? 0,
          freeDeliveryTotal: response.data.freeDeliveryTotal ?? 0,
          minOrderValue: response.data.minOrderValue ?? 0,
          minCharge: response.data.minCharge ?? 0,
          minChargeLimit: response.data.minChargeLimit ?? 0,
          perKm: response.data.perKm ?? 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpressChange = (field, value) => {
    setExpressDelivery(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExpressPauseToggle = (checked) => {
    setExpressDelivery(prev => ({
      ...prev,
      paused: checked,
      pausedAt: checked ? new Date().toISOString() : null
    }));
  };

  const handleChatChange = (field, value) => {
    setChatConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChatSave = async () => {
    try {
      if (!chatConfig.startTime || !chatConfig.endTime) {
        toast.error('Please set chat start and end time');
        return;
      }

      setChatSaving(true);
      const payload = {
        status: chatConfig.status,
        startTime: chatConfig.startTime,
        endTime: chatConfig.endTime,
      };

      const response = await settingsAPI.updateChatConfig(payload);
      if (response.success) {
        toast.success('Chat settings updated');
        setConfig(prev => ({
          ...prev,
          chatConfig: response.data || payload
        }));
        setChatConfig(mapChatFromConfig(response.data || payload));
      } else {
        toast.error(response.msg || 'Failed to update chat settings');
      }
    } catch (error) {
      console.error('Failed to save chat settings', error);
      toast.error('Failed to update chat settings');
    } finally {
      setChatSaving(false);
    }
  };

  const handleDeliveryChange = (field, value) => {
    setDeliverySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveDelivery = async () => {
    try {
      if (
        deliverySettings.maxDeliveryLimit === '' || Number(deliverySettings.maxDeliveryLimit) < 0 ||
        deliverySettings.freeDeliveryTotal === '' || Number(deliverySettings.freeDeliveryTotal) < 0 ||
        deliverySettings.minOrderValue === '' || Number(deliverySettings.minOrderValue) < 0 ||
        deliverySettings.minCharge === '' || Number(deliverySettings.minCharge) < 0 ||
        deliverySettings.minChargeLimit === '' || Number(deliverySettings.minChargeLimit) < 0 ||
        deliverySettings.perKm === '' || Number(deliverySettings.perKm) < 0
      ) {
        toast.error('Please enter valid delivery settings');
        return;
      }

      const payload = {
        maxDeliveryLimit: Number(deliverySettings.maxDeliveryLimit),
        freeDeliveryTotal: Number(deliverySettings.freeDeliveryTotal),
        minOrderValue: Number(deliverySettings.minOrderValue),
        minCharge: Number(deliverySettings.minCharge),
        minChargeLimit: Number(deliverySettings.minChargeLimit),
        perKm: Number(deliverySettings.perKm),
      };

      setDeliverySaving(true);
      const response = await settingsAPI.updateGeneralSettings(payload);
      if (response.success) {
        toast.success('Delivery settings saved successfully!');
        setConfig(prev => ({
          ...prev,
          ...payload
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save delivery settings', error);
      toast.error('Failed to save delivery settings');
    } finally {
      setDeliverySaving(false);
    }
  };

  const handleExpressSave = async () => {
    try {
      let payload;

      if (IS_ALFAJR) {
        if (
          expressDelivery.range === '' || Number(expressDelivery.range) < 0 ||
          expressDelivery.charge === '' || Number(expressDelivery.charge) < 0
        ) {
          toast.error('Please enter valid express delivery settings');
          return;
        }
        payload = {
          status: expressDelivery.status,
          startTime: expressDelivery.startTime,
          endTime: expressDelivery.endTime,
          range: Number(expressDelivery.range),
          charge: Number(expressDelivery.charge),
        };
      } else if (
        expressDelivery.staffCount === '' || Number(expressDelivery.staffCount) < 1 ||
        expressDelivery.charge1 === '' || Number(expressDelivery.charge1) < 0 ||
        expressDelivery.charge2 === '' || Number(expressDelivery.charge2) < 0 ||
        expressDelivery.charge3 === '' || Number(expressDelivery.charge3) < 0
      ) {
        toast.error('Please enter valid express delivery settings');
        return;
      } else {
        payload = {
          ...expressDelivery,
          staffCount: Number(expressDelivery.staffCount),
          charge1: Number(expressDelivery.charge1),
          charge2: Number(expressDelivery.charge2),
          charge3: Number(expressDelivery.charge3),
        };
      }

      setExpressSaving(true);

      const response = await settingsAPI.updateExpressDelivery(payload);
      if (response.success) {
        toast.success('Settings saved successfully!');
        setConfig(prev => ({
          ...prev,
          expressDelivery: IS_ALFAJR ? { ...payload } : { ...expressDelivery }
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setExpressSaving(false);
    }
  };

  const handleVersionChange = (field, value) => {
    setAppVersions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveVersions = async () => {
    try {
      const versionRegex = /^\d+(\.\d+){1,2}$/;
      if (appVersions.androidAppVersion && !versionRegex.test(appVersions.androidAppVersion)) {
        toast.error('Invalid Android version (ex: 1.13.19)');
        return;
      }
      if (appVersions.iosAppVersion && !versionRegex.test(appVersions.iosAppVersion)) {
        toast.error('Invalid iOS version (ex: 1.13.19)');
        return;
      }

      setVersionSaving(true);
      const response = await settingsAPI.updateGeneralSettings(appVersions);
      if (response.success) {
        toast.success('App versions saved successfully!');
        setConfig(prev => ({
          ...prev,
          androidAppVersion: appVersions.androidAppVersion,
          iosAppVersion: appVersions.iosAppVersion
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save app versions', error);
      toast.error('Failed to save app versions');
    } finally {
      setVersionSaving(false);
    }
  };

  const handleStoreContactChange = (field, value) => {
    setStoreContact(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStoreContactNumber = (number) => {
    if (!number) return true;
    const digits = String(number).replace(/\D/g, '');
    if (digits.length === 8) return true;
    if (digits.length === 11 && digits.startsWith('974')) return true;
    return false;
  };

  const formatStoreContactNumber = (number) => {
    if (!number) return '';
    const digits = String(number).replace(/\D/g, '');
    if (digits.length === 8) return `+974${digits}`;
    if (digits.length === 11 && digits.startsWith('974')) return `+${digits}`;
    return String(number).trim();
  };

  const handleSaveStoreContact = async () => {
    try {
      const phone = storeContact.phone.trim();
      const whatsapp = storeContact.whatsapp.trim();

      if (!phone) {
        toast.error('Phone number is required');
        return;
      }
      if (!whatsapp) {
        toast.error('WhatsApp number is required');
        return;
      }
      if (!validateStoreContactNumber(phone)) {
        toast.error('Invalid phone number. Use 8 digits or +974XXXXXXXX');
        return;
      }
      if (!validateStoreContactNumber(whatsapp)) {
        toast.error('Invalid WhatsApp number. Use 8 digits or +974XXXXXXXX');
        return;
      }

      const payload = {
        storeContact: {
          phone: formatStoreContactNumber(phone),
          whatsapp: formatStoreContactNumber(whatsapp),
        }
      };

      setStoreContactSaving(true);
      const response = await settingsAPI.updateGeneralSettings(payload);
      if (response.success) {
        toast.success('Store contact saved successfully!');
        setStoreContact(payload.storeContact);
        setConfig(prev => ({
          ...prev,
          storeContact: { ...payload.storeContact }
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save store contact', error);
      toast.error('Failed to save store contact');
    } finally {
      setStoreContactSaving(false);
    }
  };

  const handleAlertChange = (field, value) => {
    setAlerts(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAlerts = async () => {
    try {
      setAlertSaving(true);
      const adminData = authAPI.getAdminData();

      const payload = {
        webAlerts: {
          enquiryAlert: alerts.enquiryAlert,
          messageAlert: alerts.messageAlert,
          orderAlert: alerts.orderAlert,
          serverErrorAlert: alerts.serverErrorAlert
        }
      };

      const response = await authAPI.updateAdmin(adminData._id, payload);

      if (response.success) {
        toast.success('Alert settings saved successfully!');
        const updatedData = { ...adminData, webAlerts: payload.webAlerts };
        localStorage.setItem('adminData', JSON.stringify(updatedData));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save alert settings');
    } finally {
      setAlertSaving(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setCacheClearing(true);
      const response = await settingsAPI.clearRedisCache();
      if (response.success) {
        if (response.data?.cacheEnabled === false) {
          toast.success('No server cache on this environment. Nothing to clear.');
        } else {
          toast.success('Cache cleared. Homepage content will refresh on next load.');
        }
        setShowClearCacheConfirm(false);
      } else {
        toast.error(response.msg || 'Failed to clear cache');
      }
    } catch (error) {
      console.error('Failed to clear cache', error);
      toast.error('Failed to clear cache');
    } finally {
      setCacheClearing(false);
    }
  };

  const handleOtpChange = (type) => {
    if (type === 'sms') {
      setOtpConfig({ haveOtpVerification: true, whatsappOtpVerification: false });
    } else if (type === 'whatsapp') {
      setOtpConfig({ haveOtpVerification: false, whatsappOtpVerification: true });
    } else {
      setOtpConfig({ haveOtpVerification: false, whatsappOtpVerification: false });
    }
  };

  const handleCoinChange = (field, value) => {
    setCoins(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRedeemChange = (field, value) => {
    setCoins(prev => ({
      ...prev,
      redeem: {
        ...prev.redeem,
        [field]: value
      }
    }));
  };

  const isPositiveDecimal = (value) => {
    if (value === '' || value === null || value === undefined) return false;
    const num = Number(value);
    return !Number.isNaN(num) && num > 0;
  };

  const isPositiveInteger = (value) => {
    if (value === '' || value === null || value === undefined) return false;
    const num = Number(value);
    return Number.isInteger(num) && num > 0;
  };

  const buildCoinsPayload = () => {
    const base = {
      coinToQar: Number(coins.coinToQar),
      qarToCoin: Number(coins.qarToCoin),
      minCoinsRedeem: Number(coins.minCoinsRedeem),
    };
    if (IS_ALFAJR) {
      return {
        coins: {
          ...base,
          redeem: {
            limit1: Number(coins.redeem.limit1),
            limit2: Number(coins.redeem.limit2),
            redeem1: Number(coins.redeem.redeem1),
            redeem2: Number(coins.redeem.redeem2),
            redeem3: Number(coins.redeem.redeem3),
          },
        },
      };
    }
    return {
      coins: {
        ...base,
        redeemScale: Number(coins.redeemScale),
      },
    };
  };

  const validateCoins = () => {
    if (!isPositiveDecimal(coins.coinToQar)) {
      toast.error('Enter a valid Coin to QAR rate');
      return false;
    }
    if (!isPositiveDecimal(coins.qarToCoin)) {
      toast.error('Enter a valid QAR to coin rate');
      return false;
    }
    if (!isPositiveInteger(coins.minCoinsRedeem)) {
      toast.error('Enter a valid min coin redeem amount');
      return false;
    }
    if (IS_ALFAJR) {
      const { limit1, limit2, redeem1, redeem2, redeem3 } = coins.redeem;
      if (!isPositiveInteger(limit1) || !isPositiveInteger(limit2) ||
        !isPositiveInteger(redeem1) || !isPositiveInteger(redeem2) || !isPositiveInteger(redeem3)) {
        toast.error('Enter valid redeem limit and value amounts');
        return false;
      }
      if (Number(limit1) >= Number(limit2)) {
        toast.error('Redeem Limit 2 must be greater than Redeem Limit 1');
        return false;
      }
      return true;
    }
    if (!isPositiveInteger(coins.redeemScale)) {
      toast.error('Enter a valid redeem scale');
      return false;
    }
    return true;
  };

  const handleSaveCoins = async () => {
    if (!validateCoins()) return;

    try {
      setCoinSaving(true);
      const response = await settingsAPI.updateGeneralSettings(buildCoinsPayload());
      if (response.success) {
        toast.success('Coin settings saved successfully!');
        const savedCoins = mapCoinsFromConfig(response.data?.coins || buildCoinsPayload().coins);
        setCoins(savedCoins);
        setConfig(prev => ({
          ...prev,
          coins: savedCoins
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save coin settings', error);
      toast.error('Failed to save coin settings');
    } finally {
      setCoinSaving(false);
    }
  };

  const handleSaveOtp = async () => {
    try {
      setOtpSaving(true);
      const response = await settingsAPI.updateGeneralSettings(otpConfig);
      if (response.success) {
        toast.success('OTP settings saved successfully!');
        setConfig(prev => ({
          ...prev,
          ...otpConfig
        }));
      } else {
        toast.error(response.msg || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save OTP settings', error);
      toast.error('Failed to save OTP settings');
    } finally {
      setOtpSaving(false);
    }
  };

  const savedExpress = config ? mapExpressFromConfig(config.expressDelivery) : null;
  const isExpressChanged =
    !!savedExpress &&
    (
      expressDelivery.status !== savedExpress.status ||
      expressDelivery.startTime !== savedExpress.startTime ||
      expressDelivery.endTime !== savedExpress.endTime ||
      expressDelivery.paused !== savedExpress.paused ||
      expressDelivery.pausedAt !== savedExpress.pausedAt ||
      (
        IS_ALFAJR
          ? (
            Number(expressDelivery.range) !== Number(savedExpress.range) ||
            Number(expressDelivery.charge) !== Number(savedExpress.charge)
          )
          : (
            expressDelivery.radius !== savedExpress.radius ||
            expressDelivery.staffCount !== savedExpress.staffCount ||
            expressDelivery.charge1 !== savedExpress.charge1 ||
            expressDelivery.charge2 !== savedExpress.charge2 ||
            expressDelivery.charge3 !== savedExpress.charge3
          )
      )
    );

  const savedChat = config ? mapChatFromConfig(config.chatConfig) : null;
  const isChatChanged =
    !!savedChat &&
    (
      chatConfig.status !== savedChat.status ||
      chatConfig.startTime !== savedChat.startTime ||
      chatConfig.endTime !== savedChat.endTime
    );

  const isDeliveryChanged =
    !!config &&
    (
      Number(deliverySettings.maxDeliveryLimit) !== Number(config.maxDeliveryLimit ?? 0) ||
      Number(deliverySettings.freeDeliveryTotal) !== Number(config.freeDeliveryTotal ?? 0) ||
      Number(deliverySettings.minOrderValue) !== Number(config.minOrderValue ?? 0) ||
      Number(deliverySettings.minCharge) !== Number(config.minCharge ?? 0) ||
      Number(deliverySettings.minChargeLimit) !== Number(config.minChargeLimit ?? 0) ||
      Number(deliverySettings.perKm) !== Number(config.perKm ?? 0)
    );

  const isVersionChanged =
    !!config &&
    (
      appVersions.androidAppVersion !== (config.androidAppVersion ?? '') ||
      appVersions.iosAppVersion !== (config.iosAppVersion ?? '')
    );

  const isStoreContactChanged =
    !!config &&
    (
      storeContact.phone !== (config.storeContact?.phone ?? '') ||
      storeContact.whatsapp !== (config.storeContact?.whatsapp ?? '')
    );

  const isAlertChanged = () => {
    const adminData = authAPI.getAdminData();
    if (!adminData) return false;
    
    const storedWebAlerts = adminData.webAlerts || {
      enquiryAlert: true,
      messageAlert: true,
      orderAlert: true,
      serverErrorAlert: true
    };

    return (
      alerts.enquiryAlert !== (storedWebAlerts.enquiryAlert ?? true) ||
      alerts.messageAlert !== (storedWebAlerts.messageAlert ?? true) ||
      alerts.orderAlert !== (storedWebAlerts.orderAlert ?? true) ||
      alerts.serverErrorAlert !== (storedWebAlerts.serverErrorAlert ?? true)
    );
  };

  const isOtpChanged =
    !!config &&
    (
      otpConfig.haveOtpVerification !== (config.haveOtpVerification ?? true) ||
      otpConfig.whatsappOtpVerification !== (config.whatsappOtpVerification ?? false)
    );

  const configCoins = config ? mapCoinsFromConfig(config.coins) : null;
  const isCoinsChanged =
    !!configCoins &&
    (
      Number(coins.coinToQar) !== Number(configCoins.coinToQar) ||
      Number(coins.qarToCoin) !== Number(configCoins.qarToCoin) ||
      Number(coins.minCoinsRedeem) !== Number(configCoins.minCoinsRedeem) ||
      (
        IS_ALFAJR
          ? (
            Number(coins.redeem?.limit1) !== Number(configCoins.redeem?.limit1) ||
            Number(coins.redeem?.limit2) !== Number(configCoins.redeem?.limit2) ||
            Number(coins.redeem?.redeem1) !== Number(configCoins.redeem?.redeem1) ||
            Number(coins.redeem?.redeem2) !== Number(configCoins.redeem?.redeem2) ||
            Number(coins.redeem?.redeem3) !== Number(configCoins.redeem?.redeem3)
          )
          : Number(coins.redeemScale) !== Number(configCoins.redeemScale)
      )
    );

  return (
    <div className="settings-page">
      <RadiusMapModal
        isOpen={showRadiusModal}
        onClose={() => setShowRadiusModal(false)}
        currentRange={IS_ALFAJR ? expressDelivery.range : expressDelivery.radius}
        onConfirm={(value) => handleExpressChange(IS_ALFAJR ? 'range' : 'radius', value)}
      />
      <div className="settings-page-header">
        <div className="settings-page-title">
          <h1>Settings</h1>
          <p>Manage your account preferences</p>
        </div>
      </div>


      {loading ? (
        <div className="settings-loading-state">
          <div className="settings-loading-spinner"></div>
          <p>Loading Settings...</p>
        </div>
      ) : (
        <div className="settings-grid">
          <div className="settings-card">
            <div className="settings-card-header settings-card-header-toggle">
              <div className="settings-card-header-left">
                <Zap size={20} />
                <h2>Express Delivery</h2>
              </div>

              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={expressDelivery.status}
                  onChange={(e) => handleExpressChange("status", e.target.checked)}
                />
                <span className="settings-slider"></span>
              </label>
            </div>

            <div className="settings-card-content">
              <div className="settings-form-row" style={{ gridTemplateColumns: !IS_ALFAJR ? '1fr 1fr 1fr' : '1fr 1fr' }}>
                <div className="settings-form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={expressDelivery.startTime}
                    onChange={(e) => handleExpressChange('startTime', e.target.value)}
                  />
                </div>

                <div className="settings-form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={expressDelivery.endTime}
                    onChange={(e) => handleExpressChange('endTime', e.target.value)}
                  />
                </div>

                {!IS_ALFAJR && (
                  <div className="settings-form-group">
                    <label>Pause</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '40px' }}>
                      <label className="settings-toggle">
                        <input
                          type="checkbox"
                          checked={expressDelivery.paused || false}
                          onChange={(e) => handleExpressPauseToggle(e.target.checked)}
                        />
                        <span className="settings-slider"></span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {IS_ALFAJR ? (
                <div className="settings-form-row">
                  <div className="settings-form-group">
                    <label>Delivery Range</label>
                    <div className="radius-badge" onClick={() => setShowRadiusModal(true)}>
                      <span className="radius-badge-icon"><MapPin size={16} /></span>
                      {expressDelivery.range || 0} km
                    </div>
                  </div>
                  <div className="settings-form-group">
                    <label>Delivery Charge (QR)</label>
                    <input
                      type="number"
                      value={expressDelivery.charge}
                      onChange={(e) => handleExpressChange('charge', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.5"
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="settings-form-row">
                    <div className="settings-form-group">
                      <label>Delivery Radius</label>
                      <div className="radius-badge" onClick={() => setShowRadiusModal(true)}>
                        <span className="radius-badge-icon"><MapPin size={16} /></span>
                        {expressDelivery.radius || 0} km
                      </div>
                    </div>
                    <div className="settings-form-group">
                      <label>Staff Count</label>
                      <input
                        type="number"
                        value={expressDelivery.staffCount}
                        onChange={(e) => handleExpressChange('staffCount', e.target.value === '' ? '' : Number(e.target.value))}
                        min="1"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>

                  <div className="settings-form-row">
                    <div className="settings-form-group">
                      <label>{`Charge (QR 0 to QR ${config?.expressDelivery?.range1 ?? 49})`}</label>
                      <input
                        type="number"
                        value={expressDelivery.charge1}
                        onChange={(e) => handleExpressChange('charge1', e.target.value === '' ? '' : Number(e.target.value))}
                        min="0"
                        step="0.5"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    <div className="settings-form-group">
                      <label>{`Charge (QR ${(config?.expressDelivery?.range1 ?? 49) + 1} to QR ${config?.expressDelivery?.range2 ?? 99})`}</label>
                      <input
                        type="number"
                        value={expressDelivery.charge2}
                        onChange={(e) => handleExpressChange('charge2', e.target.value === '' ? '' : Number(e.target.value))}
                        min="0"
                        step="0.5"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    <div className="settings-form-group">
                      <label>{`Charge (Above QR ${config?.expressDelivery?.range2 ?? 99})`}</label>
                      <input
                        type="number"
                        value={expressDelivery.charge3}
                        onChange={(e) => handleExpressChange('charge3', e.target.value === '' ? '' : Number(e.target.value))}
                        min="0"
                        step="0.5"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>
                </>
              )}

              {canManage && (
                <button className="settings-save-btn" onClick={handleExpressSave} disabled={expressSaving || !isExpressChanged}>
                  <Save size={16} />
                  {expressSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {/* Chat Support Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header settings-card-header-toggle">
              <div className="settings-card-header-left">
                <MessageCircle size={20} />
                <h2>Chat Support</h2>
              </div>

              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={chatConfig.status}
                  onChange={(e) => handleChatChange('status', e.target.checked)}
                  disabled={!canManage}
                />
                <span className="settings-slider"></span>
              </label>
            </div>

            <div className="settings-card-content">
              <p className="settings-card-hint">
                When enabled, chat is available only between the start and end times below.
              </p>
              <div className="settings-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="settings-form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={chatConfig.startTime}
                    onChange={(e) => handleChatChange('startTime', e.target.value)}
                    disabled={!canManage}
                  />
                </div>

                <div className="settings-form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={chatConfig.endTime}
                    onChange={(e) => handleChatChange('endTime', e.target.value)}
                    disabled={!canManage}
                  />
                </div>
              </div>

              {canManage && (
                <button className="settings-save-btn" onClick={handleChatSave} disabled={chatSaving || !isChatChanged}>
                  <Save size={16} />
                  {chatSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {/* Delivery Settings Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-header-left">
                <MapPin size={20} />
                <h2>Delivery Settings</h2>
              </div>
            </div>

            <div className="settings-card-content">
              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>Max Delivery Limit (KM)</label>
                  <input
                    type="number"
                    value={deliverySettings.maxDeliveryLimit}
                    onChange={(e) => handleDeliveryChange('maxDeliveryLimit', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>

                <div className="settings-form-group">
                  <label>Free Delivery Total (QR)</label>
                  <input
                    type="number"
                    value={deliverySettings.freeDeliveryTotal}
                    onChange={(e) => handleDeliveryChange('freeDeliveryTotal', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>

              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>Min Order Value (QR)</label>
                  <input
                    type="number"
                    value={deliverySettings.minOrderValue}
                    onChange={(e) => handleDeliveryChange('minOrderValue', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>

                <div className="settings-form-group">
                  <label>Min Delivery Charge (QR)</label>
                  <input
                    type="number"
                    value={deliverySettings.minCharge}
                    onChange={(e) => handleDeliveryChange('minCharge', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>

              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>Min Charge Upto Km</label>
                  <input
                    type="number"
                    value={deliverySettings.minChargeLimit}
                    onChange={(e) => handleDeliveryChange('minChargeLimit', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>

                <div className="settings-form-group">
                  <label>Per Km Charge (QR)</label>
                  <input
                    type="number"
                    value={deliverySettings.perKm}
                    onChange={(e) => handleDeliveryChange('perKm', e.target.value === '' ? '' : Number(e.target.value))}
                    min="0"
                    step="0.01"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
              </div>

              {canManage && (
                <button
                  className="settings-save-btn"
                  onClick={handleSaveDelivery}
                  disabled={deliverySaving || !isDeliveryChanged}
                >
                  <Save size={16} />
                  {deliverySaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {isFajr2 && (
            <div className="settings-card">
              <div className="settings-card-header">
                <div className="settings-card-header-left">
                  <Smartphone size={20} />
                  <h2>App Versions</h2>
                </div>
              </div>

              <div className="settings-card-content">
                <div className="settings-form-row">
                  <div className="settings-form-group">
                    <label>Android Version</label>
                    <input
                      type="text"
                      placeholder="1.13.19"
                      value={appVersions.androidAppVersion}
                      onChange={(e) => handleVersionChange('androidAppVersion', e.target.value)}
                    />
                  </div>

                  <div className="settings-form-group">
                    <label>iOS Version</label>
                    <input
                      type="text"
                      placeholder="1.13.19"
                      value={appVersions.iosAppVersion}
                      onChange={(e) => handleVersionChange('iosAppVersion', e.target.value)}
                    />
                  </div>
                </div>

                {canManage && (
                  <button className="settings-save-btn" onClick={handleSaveVersions} disabled={versionSaving || !isVersionChanged}>
                    <Save size={16} />
                    {versionSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          )}

          {isFajr2 && (
            <div className="settings-card">
              <div className="settings-card-header">
                <div className="settings-card-header-left">
                  <Coins size={20} />
                  <h2>Coin Settings</h2>
                </div>
              </div>

              <div className="settings-card-content">
                <div className="settings-form-row">
                  <div className="settings-form-group">
                    <label>Coin to QAR</label>
                    <input
                      type="number"
                      value={coins.coinToQar}
                      onChange={(e) => handleCoinChange('coinToQar', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>

                  <div className="settings-form-group">
                    <label>Qar to coin</label>
                    <input
                      type="number"
                      value={coins.qarToCoin}
                      onChange={(e) => handleCoinChange('qarToCoin', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>

                <div className="settings-form-row">
                  <div className="settings-form-group">
                    <label>Min Coin Redeem</label>
                    <input
                      type="number"
                      value={coins.minCoinsRedeem}
                      onChange={(e) => handleCoinChange('minCoinsRedeem', e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="1"
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>

                  {!IS_ALFAJR && (
                    <div className="settings-form-group">
                      <label>Redeem Scale</label>
                      <input
                        type="number"
                        value={coins.redeemScale}
                        onChange={(e) => handleCoinChange('redeemScale', e.target.value === '' ? '' : Number(e.target.value))}
                        min="0"
                        step="1"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  )}
                </div>

                {IS_ALFAJR && (
                  <>
                    <div className="settings-form-row">
                      <div className="settings-form-group">
                        <label>Redeem Limit 1</label>
                        <input
                          type="number"
                          value={coins.redeem?.limit1}
                          onChange={(e) => handleRedeemChange('limit1', e.target.value === '' ? '' : Number(e.target.value))}
                          min="0"
                          step="1"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                      <div className="settings-form-group">
                        <label>Redeem Value 1</label>
                        <input
                          type="number"
                          value={coins.redeem?.redeem1}
                          onChange={(e) => handleRedeemChange('redeem1', e.target.value === '' ? '' : Number(e.target.value))}
                          min="0"
                          step="1"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    </div>

                    <div className="settings-form-row">
                      <div className="settings-form-group">
                        <label>Redeem Limit 2</label>
                        <input
                          type="number"
                          value={coins.redeem?.limit2}
                          onChange={(e) => handleRedeemChange('limit2', e.target.value === '' ? '' : Number(e.target.value))}
                          min="0"
                          step="1"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                      <div className="settings-form-group">
                        <label>Redeem Value 2</label>
                        <input
                          type="number"
                          value={coins.redeem?.redeem2}
                          onChange={(e) => handleRedeemChange('redeem2', e.target.value === '' ? '' : Number(e.target.value))}
                          min="0"
                          step="1"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    </div>

                    <div className="settings-form-row">
                      <div className="settings-form-group settings-form-group--redeem-max">
                        <label>Max Redeem Value</label>
                        <input
                          type="number"
                          value={coins.redeem?.redeem3}
                          onChange={(e) => handleRedeemChange('redeem3', e.target.value === '' ? '' : Number(e.target.value))}
                          min="0"
                          step="1"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    </div>
                  </>
                )}

                {canManage && (
                  <button className="settings-save-btn" onClick={handleSaveCoins} disabled={coinSaving || !isCoinsChanged}>
                    <Save size={16} />
                    {coinSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          )}

          <TimeSlotsSettings
            defaultTimeSlots={config?.defaultTimeSlots || []}
            timeSlotsList={config?.timeSlotsList || []}
            canManage={canManage}
            onSaved={fetchGeneralConfigs}
          />

          {/* Store Contact Card */}
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-header-left">
                <Phone size={20} />
                <h2>Store Contact</h2>
              </div>
            </div>

            <div className="settings-card-content">
              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    placeholder="+97400000000"
                    value={storeContact.phone}
                    onChange={(e) => handleStoreContactChange('phone', e.target.value)}
                  />
                </div>

                <div className="settings-form-group">
                  <label>WhatsApp Number</label>
                  <input
                    type="text"
                    placeholder="+97400000000"
                    value={storeContact.whatsapp}
                    onChange={(e) => handleStoreContactChange('whatsapp', e.target.value)}
                  />
                </div>
              </div>

              {canManage && (
                <button className="settings-save-btn" onClick={handleSaveStoreContact} disabled={storeContactSaving || !isStoreContactChanged}>
                  <Save size={16} />
                  {storeContactSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {isFajr2 && (
            <div className="settings-card">
              <div className="settings-card-header">
                <div className="settings-card-header-left">
                  <ShieldCheck size={20} />
                  <h2>OTP Verification</h2>
                </div>
              </div>

              <div className="settings-card-content">
                <div className="settings-otp-options">
                  <label className="settings-radio-group">
                    <input
                      type="radio"
                      name="otpType"
                      checked={otpConfig.haveOtpVerification && !otpConfig.whatsappOtpVerification}
                      onChange={() => handleOtpChange('sms')}
                    />
                    <span className="radio-label">SMS Otp</span>
                  </label>

                  <label className="settings-radio-group">
                    <input
                      type="radio"
                      name="otpType"
                      checked={!otpConfig.haveOtpVerification && otpConfig.whatsappOtpVerification}
                      onChange={() => handleOtpChange('whatsapp')}
                    />
                    <span className="radio-label">Whatsapp Otp</span>
                  </label>

                  <label className="settings-radio-group">
                    <input
                      type="radio"
                      name="otpType"
                      checked={!otpConfig.haveOtpVerification && !otpConfig.whatsappOtpVerification}
                      onChange={() => handleOtpChange('none')}
                    />
                    <span className="radio-label">No Otp Verification</span>
                  </label>
                </div>

                {canManage && (
                  <button className="settings-save-btn" onClick={handleSaveOtp} disabled={otpSaving || !isOtpChanged}>
                    <Save size={16} />
                    {otpSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-header-left">
                <Smartphone size={20} />
                <h2>Alerts</h2>
              </div>
            </div>

            <div className="settings-card-content">
              <div className="settings-alert-row">
                <span>Enquiry Alert</span>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={alerts.enquiryAlert}
                    onChange={(e) =>
                      handleAlertChange('enquiryAlert', e.target.checked)
                    }
                  />
                  <span className="settings-slider"></span>
                </label>
              </div>

              <div className="settings-alert-row">
                <span>Message Alert</span>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={alerts.messageAlert}
                    onChange={(e) =>
                      handleAlertChange('messageAlert', e.target.checked)
                    }
                  />
                  <span className="settings-slider"></span>
                </label>
              </div>

              <div className="settings-alert-row">
                <span>Order Alert</span>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={alerts.orderAlert}
                    onChange={(e) =>
                      handleAlertChange('orderAlert', e.target.checked)
                    }
                  />
                  <span className="settings-slider"></span>
                </label>
              </div>
              <div className="settings-alert-row">
                <span>Server Alert</span>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={alerts.serverErrorAlert}
                    onChange={(e) =>
                      handleAlertChange('serverErrorAlert', e.target.checked)
                    }
                  />
                  <span className="settings-slider"></span>
                </label>
              </div>

              {canManage && (
                <button
                  className="settings-save-btn"
                  onClick={handleSaveAlerts}
                  disabled={alertSaving || !isAlertChanged()}
                  style={{ marginTop: "5px" }}
                >
                  <Save size={16} />
                  {alertSaving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>

          {canManage && (
            <div className="settings-card">
              <div className="settings-card-header">
                <div className="settings-card-header-left">
                  <Database size={20} />
                  <h2>Cache</h2>
                </div>
              </div>

              <div className="settings-card-content">
                <p className="settings-card-hint">
                  Homepage content is cached on the server. Clear it if an update
                  from the dashboard is not visible in the app.
                </p>

                <ul className="settings-cache-list">
                  <li>Homepage categories &amp; images</li>
                  <li>Banners, sliders &amp; trending cards</li>
                  <li>Deal zone products (homepage &amp; category page)</li>
                  <li>Reel cards &amp; trending searches</li>
                </ul>

                <button
                  className="settings-danger-btn"
                  onClick={() => setShowClearCacheConfirm(true)}
                  disabled={cacheClearing}
                >
                  <Database size={16} />
                  {cacheClearing ? 'Clearing...' : 'Clear Cache'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showClearCacheConfirm && (
        <div className="settings-confirm-overlay">
          <div className="settings-confirm-modal">
            <div className="settings-confirm-header">
              <div className="settings-confirm-title">
                <AlertTriangle size={20} />
                <h2>Clear Cache</h2>
              </div>
              <button
                className="settings-confirm-close"
                onClick={() => setShowClearCacheConfirm(false)}
                disabled={cacheClearing}
              >
                <X size={20} />
              </button>
            </div>

            <div className="settings-confirm-content">
              <p>
                This clears the cached homepage categories, banners, deal zone
                products, reel cards and trending searches. Customers will see the
                latest content on their next app load.
              </p>
            </div>

            <div className="settings-confirm-footer">
              <button
                className="settings-confirm-cancel-btn"
                onClick={() => setShowClearCacheConfirm(false)}
                disabled={cacheClearing}
              >
                Cancel
              </button>
              <button
                className="settings-confirm-btn"
                onClick={handleClearCache}
                disabled={cacheClearing}
              >
                {cacheClearing ? 'Clearing...' : 'Clear Cache'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;